import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Heading } from '../components/Heading';
import { theme } from '../styles/GlobalStyles';

const ContactHero = styled.div`
  position: relative;
  width: 100%;
  height: 60vh;
  min-height: 400px;
  padding-top: 100px; /* Account for standard navbar */
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const ContactHeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 100%);
  z-index: 1;
`;

const ContactHeroBg = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  background: url('/img_to_use/contact.jpg') center/cover no-repeat;
  transform: scale(1);
  animation: zoomContactHero 18s ease-in-out infinite alternate;
  will-change: transform;
  transition: transform 0.7s cubic-bezier(0.4,0,0.2,1);

  @keyframes zoomContactHero {
    0% { transform: scale(1);}
    100% { transform: scale(1.08);}
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      animation-play-state: paused;
      transform: scale(1.16);
      transition: transform 0.7s cubic-bezier(0.4,0,0.2,1);
    }
  }
`;

const ContactHeroContent = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  color: #fff;
  padding: 0 1rem;
`;

const ContactHeroTitle = styled.h1`
  font-size: 4.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.6), 0 0 12px ${theme.colors.accent}33;
  color: ${theme.colors.accent};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 2.8rem;
  }
`;

const ContactHeroSubtitle = styled.p`
  font-size: 1.6rem;
  margin-bottom: 2.5rem;
  font-weight: 300;
  text-shadow: 1px 1px 6px rgba(0, 0, 0, 0.8);
  color: #FFFFFF;
  opacity: 0.95;
`;

const ContactSection = styled.section`
  background: ${theme.colors.background};
  width: 100%;
  padding: clamp(4rem, 8vw, 8rem) 0;
  text-align: center;
`;

const ContactForm = styled.form`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  &:last-child {
    grid-column: 1 / -1;
  }
`;

const CheckboxGroup = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin: 1rem 0;
`;

const Checkbox = styled.input`
  margin-top: 0.25rem;
  width: 18px;
  height: 18px;
  accent-color: ${theme.colors.accent};
`;

const CheckboxLabel = styled.label`
  font-size: 0.9rem;
  line-height: 1.5;
  color: #495057;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  
  a {
    color: ${theme.colors.primary};
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ButtonGroup = styled.div`
  grid-column: 1 / -1;
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 1rem;
`;

const Label = styled.label`
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #22313a;
  font-family: 'Inter', sans-serif;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  font-family: 'Inter', sans-serif;
  transition: border-color 0.3s ease;
  &:focus {
    outline: none;
    border-color: ${theme.colors.accent};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  height: 150px;
  padding: 0.8rem 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  font-family: 'Inter', sans-serif;
  resize: vertical;
  transition: border-color 0.3s ease;
  &:focus {
    outline: none;
    border-color: ${theme.colors.accent};
  }
`;


const SubmitButton = styled.button`
  background: ${theme.colors.accent};
  color: #22313a;
  border: none;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  font-family: 'Inter', sans-serif;
  margin-right: 1rem;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${theme.colors.accent}4d;
  }
`;

const CodeOfConductButton = styled.button`
  background: transparent;
  color: ${theme.colors.primary};
  border: 2px solid ${theme.colors.primary};
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Inter', sans-serif;
  &:hover {
    background: ${theme.colors.primary};
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(31, 58, 95, 0.3);
  }
`;

const ContactInfoSection = styled.section`
  background: ${theme.colors.lightGray};
  width: 100%;
  padding: clamp(4rem, 8vw, 8rem) 0;
  text-align: center;
`;

const ContactInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: clamp(2rem, 4vw, 3rem);
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const ContactInfoCard = styled(motion.div)`
  position: relative;
  height: auto;
  min-height: 320px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 2.5rem;
  overflow: hidden;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(34,49,58,0.10);
  transition: transform 0.3s cubic-bezier(0.4,0,0.2,1), box-shadow 0.3s cubic-bezier(0.4,0,0.2,1);
  will-change: transform, box-shadow;
  &:hover {
    transform: scale(1.03) translateY(-8px);
    box-shadow: 0 8px 32px ${theme.colors.accent}2e, 0 2px 12px rgba(34,49,58,0.13);
    z-index: 2;
  }
  .icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: ${theme.colors.accent}22;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2.4rem;
    color: ${theme.colors.accent};
    margin-bottom: 1.5rem;
  }
  h3 {
    color: ${theme.colors.accent};
    font-size: 1.8rem;
    margin-bottom: 1.2rem;
    font-weight: 900;
    letter-spacing: 1.2px;
    font-family: 'Playfair Display', serif;
  }
  p {
    color: #22313a;
    font-size: 1.15rem;
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    line-height: 1.8;
    white-space: pre-line;
  }
`;

const MapSection = styled.section`
  background: #fff;
  width: 100%;
  padding: clamp(4rem, 8vw, 8rem) 0;
  text-align: center;
`;

const MapContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  height: 500px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(34,49,58,0.10);
  border: 1px solid rgba(255,215,0,0.2);
`;

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    optIn: false
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const mailtoLink = `mailto:Support@dearcglobal.com?subject=${encodeURIComponent(formData.subject)}&body=${encodeURIComponent(
      `Name: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\n\nMessage:\n${formData.message}`
    )}`;
    window.location.href = mailtoLink;
  };

  const contactInfo = [
    {
      title: 'Head Office',
      icon: 'location',
      desc: 'Canton, Michigan 48186\nUnited States'
    },
    {
      title: 'Contact Us',
      icon: 'phone',
      desc: 'Contact: Mr Davinder Grewal\nPhone: +1 734-560-2886\nEmail: info@dearcglobal.com'
    },
    {
      title: 'Business Hours',
      icon: 'clock',
      desc: 'Monday - Friday: 9:00 AM - 6:00 PM\nSaturday - Sunday: Closed'
    }
  ];

  return (
    <>
      <ContactHero>
        <ContactHeroBg />
        <ContactHeroOverlay />
        <ContactHeroContent>
          <ContactHeroTitle>Contact Us</ContactHeroTitle>
          <ContactHeroSubtitle>
            Get in touch with our team in Canton, Michigan
          </ContactHeroSubtitle>
        </ContactHeroContent>
      </ContactHero>

      <ContactInfoSection>
        <Heading level="h2" align="center" style={{ marginBottom: '3rem' }}>
          Contact Information
        </Heading>
        <ContactInfoGrid>
          {contactInfo.map((info, idx) => (
            <ContactInfoCard
              key={info.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, delay: idx * 0.1 }}
            >
              <div className="icon">
                {info.icon === 'location' ? '📍' : info.icon === 'phone' ? '☎️' : '⏰'}
              </div>
              <h3>{info.title}</h3>
              <p>{info.desc}</p>
            </ContactInfoCard>
          ))}
        </ContactInfoGrid>
      </ContactInfoSection>

      <MapSection>
        <Heading level="h2" align="center" style={{ marginBottom: '3rem' }}>
          Our Location
        </Heading>
        <MapContainer>
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3008.443928665799!2d-83.4824!3d42.3089!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x883b4d6e507a5d07%3A0x8eef0cd2bdfa4a6a!2sCanton%2C%20MI%2048186!5e0!3m2!1sen!2sus!4v1647881234567!5m2!1sen!2sus"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </MapContainer>
      </MapSection>

      <ContactSection>
        <Heading level="h2" align="center" style={{ marginBottom: '3rem' }}>
          Send Us a Message
        </Heading>
        <ContactForm onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="name">Name</Label>
            <Input
              type="text"
              id="name"
              placeholder="Your name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="email">Email</Label>
            <Input
              type="email"
              id="email"
              placeholder="Your email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="phone">Phone Number (Optional)</Label>
            <Input
              type="tel"
              id="phone"
              placeholder="Your phone number"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="subject">Subject</Label>
            <Input
              type="text"
              id="subject"
              placeholder="Subject"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              required
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="message">Message</Label>
            <TextArea
              id="message"
              placeholder="Your message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              required
            />
          </FormGroup>
          
          <CheckboxGroup>
            <Checkbox
              type="checkbox"
              id="optIn"
              checked={formData.optIn}
              onChange={(e) => setFormData({ ...formData, optIn: e.target.checked })}
            />
            <CheckboxLabel htmlFor="optIn">
              By opting in for text messages, you agree to receive appointment reminders and important updates from De ARC Global LLC at the number provided. Message frequency varies. Msg & data rates may apply. Reply STOP to unsubscribe. Reply HELP for help. View our <a href="/privacy-policy">Privacy Policy</a>, <a href="/terms-conditions">Terms & Conditions</a>, and <a href="/code-of-conduct">Code of Conduct</a> for more information.
            </CheckboxLabel>
          </CheckboxGroup>
          
          <ButtonGroup>
            <SubmitButton type="submit">Send Message</SubmitButton>
            <CodeOfConductButton 
              type="button" 
              onClick={() => window.location.href = '/code-of-conduct'}
            >
              View Code of Conduct
            </CodeOfConductButton>
          </ButtonGroup>
        </ContactForm>
      </ContactSection>
    </>
  );
};

export default ContactPage; 